import { atom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { resourcesAtom } from 'atoms/root';
import request from 'lib/request';
import type {
  Establishment,
  EstablishmentsResponse,
  ReferenceLocationGeometry,
  DocumentLocation,
} from 'types/api-responses';

// 1. Data atom - always an array (normalized from API)
export const establishmentsDataAtom = atom<Establishment[] | null>(null);

// 2. Loading state atom
export const establishmentsLoadingAtom = atom(false);

// Geometry data atoms (populated by fetch)
const referenceLocationGeometriesDataAtom = atom<ReferenceLocationGeometry[]>(
  []
);
const documentLocationsDataAtom = atom<DocumentLocation[]>([]);

// 3. Fetch atom - document-scoped only
export const fetchEstablishmentsAtom = atom(
  null,
  async (get, set, params?: { documentId?: number }) => {
    const resources = get(resourcesAtom);
    const href = resources?.establishments?.href;

    if (!href || !params?.documentId) return;

    set(establishmentsLoadingAtom, true);

    try {
      const url = `${href}?document_id=${params.documentId}`;
      const response = await request.get<EstablishmentsResponse>(
        url,
        new AbortController()
      );
      if ('error' in response) {
        console.error('Failed to fetch establishments:', response.error);
      } else {
        set(establishmentsDataAtom, response.establishments ?? []);
        set(
          referenceLocationGeometriesDataAtom,
          response.referenceLocations ?? []
        );
        set(documentLocationsDataAtom, response.documentLocations ?? []);
      }
    } catch (error) {
      console.error('Failed to fetch establishments:', error);
    } finally {
      set(establishmentsLoadingAtom, false);
    }
  }
);

// 4. Derived atom - same array (for consistent API with consumers)
export const establishmentsArrayAtom = atom((get) => {
  const data = get(establishmentsDataAtom);
  return data ?? [];
});

// 5. Derived atom - reference location geometries
export const referenceLocationGeometriesAtom = atom((get) =>
  get(referenceLocationGeometriesDataAtom)
);

// 6. Derived atom - document location geometries
export const documentLocationsAtom = atom((get) =>
  get(documentLocationsDataAtom)
);

// 7. Shared hover state for bidirectional table <-> map interaction
export const hoveredEstablishmentRefLocationIdAtom = atom<number | null>(null);

export const selectedEstablishmentIdsAtom = atom<number[] | null>(null);

export const updateEstablishmentsMutationAtom = createMutationAtom<
  EstablishmentsResponse,
  'establishment'
>();

export const deleteEstablishmentsMutationAtom = createMutationAtom<
  EstablishmentsResponse,
  'establishment'
>();

export const buildEstablishmentsUrl = (
  baseHref: string,
  referenceLocationId: number | string
) => {
  const queryParams = new URLSearchParams({
    reference_location_id: String(referenceLocationId),
  });
  return `${baseHref}?${queryParams}`;
};

export const mergeEstablishmentsAtom = atom(
  null,
  (get, set, updatedEstablishments: Establishment[]) => {
    const currentEstablishments = get(establishmentsDataAtom);
    if (!currentEstablishments) return;

    const updatedIds = new Set(updatedEstablishments.map((e) => e.id));
    const existingEstablishmentsMap = new Map(
      currentEstablishments.map((e) => [e.id, e])
    );

    const mergedEstablishments = currentEstablishments
      .filter((e) => !updatedIds.has(e.id))
      .concat(
        updatedEstablishments.map((updated) => {
          const existing = existingEstablishmentsMap.get(updated.id);
          return {
            ...updated,
            documentLocationIntersections:
              existing?.documentLocationIntersections ||
              updated.documentLocationIntersections,
          };
        })
      );

    set(establishmentsDataAtom, mergedEstablishments);
  }
);

export const removeEstablishmentsAtom = atom(
  null,
  (
    get,
    set,
    params: {
      referenceLocationId: number;
      establishmentDate?: string;
      entityId?: number;
    }
  ) => {
    const currentEstablishments = get(establishmentsDataAtom);
    if (!currentEstablishments) return;

    const filteredEstablishments = currentEstablishments.filter((e) => {
      if (e.referenceLocationId !== params.referenceLocationId) return true;
      if (
        params.establishmentDate &&
        e.establishmentDate !== params.establishmentDate
      )
        return true;
      if (params.entityId && e.entityId !== params.entityId) return true;
      return false;
    });

    set(establishmentsDataAtom, filteredEstablishments);
  }
);
